import React, { useEffect, useRef, useState } from "react";
import zeroTypingLogo from "../../Asset/ZEROTyingLogo.svg";
import { ReactSVG } from "react-svg";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import Profile from "../Profile/Profile";
import { HiOutlineMenuAlt4 } from "react-icons/hi";
import { clearUser, selectIsLoggedIn } from "../../Redux/authSlice";
import { useDispatch, useSelector } from "react-redux";
import { FiLogOut, FiX, FiChevronDown, FiArrowRight } from "react-icons/fi";
import { SiCircle } from "react-icons/si";
import ManageSubscription from "../../Asset/Manage Subscription.svg";
import sideBarLogo from "../../Asset/ZT-Sidebar-logo.svg";
import gradientArrow from "../../Asset/gradient arrow.svg";
import { toast } from "react-toastify";
import axios from "axios";
import NavDownloadButton from "../DownloadButton/NavDownloadButton";

const pages = [
  { name: "Home", link: "/" },
  { name: "Demo", link: "/demo" },
  { name: "Pricing", link: "/pricing" },
  { name: "Help", link: "/help" },
  // Blogs intentionally absent — it lives in the footer (component/Footer/Footer.js).
  { name: "App", link: "/app" },
];

/**
 * The "Use Cases" dropdown. These are the marketing landing pages that had no
 * entry anywhere in the nav before — they were reachable only by direct URL,
 * which cost them both a user path and the internal links that help them rank.
 *
 * Demo and Pricing are deliberately absent: they already sit top-level above.
 */
const useCases = [
  { name: "Google Docs", link: "/voice-typing-for-google-docs" },
  { name: "Slack & Notion", link: "/voice-typing-for-slack-and-notion" },
  { name: "AI chat prompts", link: "/voice-typing-for-ai-chat-prompts" },
  { name: "AI prompting", link: "/voice-prompting-ai" },
  { name: "How it works", link: "/how-it-works" },
  { name: "Windows", link: "/windows" },
  { name: "Mac", link: "/mac" },
  { name: "Download", link: "/download" },
  { name: "How AI voice typing works", link: "/how-ai-voice-typing-works" },
];

const Navbar = () => {
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const [showNavbar, setShowNavbar] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [useCasesOpen, setUseCasesOpen] = useState(false);
  const [mobileUseCasesOpen, setMobileUseCasesOpen] = useState(false);
  const useCasesRef = useRef(null);

  const isUseCaseRoute = useCases.some((item) => location.pathname === item.link);

  const isSettingsPage = location.pathname.startsWith("/settings");
  const isWebAppPage = location.pathname.startsWith("/app");
  const isUsagePage = location.pathname.startsWith("/usage");
  const isSignupSuccessPage = location.pathname.startsWith("/signup-successful");

  const visiblePages = isSignupSuccessPage
    ? pages.filter((p) => p.name !== "App")
    : pages;

  const handleScroll = () => {
    const currentScrollY = window.scrollY;

    if (currentScrollY > lastScrollY && currentScrollY > 50) {
      setShowNavbar(false);
    } else if (currentScrollY < lastScrollY) {
      setShowNavbar(true);
    }

    setLastScrollY(currentScrollY);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastScrollY]);

  // Dismiss the desktop dropdown on an outside click or Escape. Bound only
  // while it is open so the listeners cost nothing the rest of the time.
  useEffect(() => {
    if (!useCasesOpen) return undefined;

    const onPointerDown = (e) => {
      if (useCasesRef.current && !useCasesRef.current.contains(e.target)) {
        setUseCasesOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setUseCasesOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [useCasesOpen]);

  // Navigating away closes both menus — clicking a dropdown item should not
  // leave the panel hanging open over the page it just took you to.
  useEffect(() => {
    setUseCasesOpen(false);
    setMobileUseCasesOpen(false);
  }, [location.pathname]);

  // The whole navbar slides out of view on scroll-down. An open panel would
  // ride off-screen with it, so close it the moment that happens.
  useEffect(() => {
    if (!showNavbar) setUseCasesOpen(false);
  }, [showNavbar]);

  // Closing the mobile drawer collapses its accordion, so reopening the drawer
  // always starts from the same state.
  useEffect(() => {
    if (!isOpen) setMobileUseCasesOpen(false);
  }, [isOpen]);

  const handleLogout = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}api/auth/logout`,
        {},
        { withCredentials: true }
      );

      if (response?.data?.success) {
        toast.success(response.data?.message, { toastId: "logout" });
        dispatch(clearUser());
        navigate("/");
      }
    } catch (error) {
      toast.error(error?.response?.data?.error || error?.response?.data?.message || "Logout failed", { toastId: "logout" });
    }
  };

  return (
    <div
      className={`navbar ${showNavbar ? "show" : "hide"
        } w-full transition-transform duration-300 z-50 font-inter`}
    >
      <div className="relative">
        {/* Top Bar */}
        <div className="p-3 flex items-center justify-between mx-auto md:mt-8 mt-4 w-[95%] max-w-[1450px] md:bg-[#FFFFFF4D] bg-[#FFFFFF33] shadow-md backdrop-blur-md md:rounded-full rounded-2xl">
          <NavLink to="/" aria-label="Zero Typing AI home" className="w-auto shrink-0">
            <ReactSVG
              src={zeroTypingLogo}
              beforeInjection={(svg) => {
                svg.setAttribute("class", "w-auto lg:h-12 md:h-9 h-8");
              }}
              className="w-auto"
            />
          </NavLink>

          {/* Nav Links */}
          <div className="hidden md:flex items-center lg:gap-5 gap-3 lg:text-[1.2rem] text-base font-medium">
            {visiblePages?.map((page, i) => (
              <NavLink
                to={page.link}
                key={i}
                className={({ isActive }) =>
                  isActive ? "text-[#3AB1F6]" : "text-[#00000080]"
                }
              >
                {page.name}
              </NavLink>
            ))}

            {/* Use Cases dropdown */}
            <div className="relative" ref={useCasesRef}>
              <button
                type="button"
                onClick={() => setUseCasesOpen((open) => !open)}
                aria-expanded={useCasesOpen}
                aria-haspopup="true"
                className={`flex items-center gap-1 whitespace-nowrap transition-colors duration-200 ${
                  useCasesOpen || isUseCaseRoute
                    ? "text-[#3AB1F6]"
                    : "text-[#00000080] hover:text-[#3AB1F6]"
                }`}
              >
                Use Cases
                <FiChevronDown
                  className={`size-4 transition-transform duration-200 ${
                    useCasesOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {useCasesOpen && (
                <div className="absolute left-1/2 top-full z-50 mt-4 w-[280px] -translate-x-1/2">
                  {/* Caret. Drawn first so the panel below paints over its
                      lower half, leaving just the point showing. */}
                  <div className="absolute -top-[6px] left-1/2 h-3.5 w-3.5 -translate-x-1/2 rotate-45 rounded-[3px] bg-white" />

                  <div className="relative overflow-hidden rounded-2xl bg-white py-2 shadow-[0_14px_44px_rgba(10,13,22,0.16)]">
                    {useCases.map((item) => (
                      <NavLink
                        key={item.link}
                        to={item.link}
                        onClick={() => setUseCasesOpen(false)}
                        className={({ isActive }) =>
                          `flex items-center justify-between gap-4 px-5 py-3 text-[15px] font-medium leading-tight transition-colors duration-150 ${
                            isActive
                              ? "bg-[#F1F8FD] text-[#3AB1F6]"
                              : "text-[#0A0D16] hover:bg-[#F1F8FD]"
                          }`
                        }
                      >
                        <span>{item.name}</span>
                        <FiArrowRight className="size-4 shrink-0 text-[#3AB1F6]" />
                      </NavLink>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Side */}
          <div className="hidden md:flex items-center gap-2">
            {isLoggedIn ? (
              <>
                <NavDownloadButton
                  location="navbar_desktop"
                  className="lg:text-[15px] md:text-sm text-xs h-11 px-4"
                  iconClassName="w-5 h-5"
                />
                <Profile />
              </>
            ) : (
              <>
                <NavLink
                  to="/login"
                  className="bg-[#D8EFFD] border border-[#3AB1F626] hover:bg-[#C9E7FB] transition-all duration-300 ease-in-out text-[#3AB1F6] lg:text-[1.15rem] text-base font-semibold h-11 flex justify-center items-center px-6 rounded-full"
                >
                  Sign In
                </NavLink>
                <NavDownloadButton
                  location="navbar_desktop"
                  className="lg:text-[15px] md:text-sm text-xs h-11 px-4"
                  iconClassName="w-5 h-5"
                />
              </>
            )}
          </div>

          {/* Mobile */}
          <div className="flex md:hidden items-center gap-2">
            <NavDownloadButton
              location="navbar_mobile"
              compact
              className="text-xs w-max py-2.5 px-4 leading-none"
              iconClassName="w-4 h-4"
            />
            <HiOutlineMenuAlt4 onClick={() => setIsOpen(true)} className="size-5" />
          </div>
        </div>

        {/* Mobile Drawer.
            max-h + overflow-y: the navbar is position:fixed, so this drawer
            cannot scroll with the page. Once Use Cases is expanded the content
            can exceed a phone screen, and without this the items below it
            would be unreachable. */}
        <div
          className={`absolute md:hidden inset-y-0 right-0 left-0 -top-5 p-4 pt-8 rounded-b-xl w-full h-max max-h-[88vh] overflow-y-auto overscroll-contain shadow-md bg-white z-50 transition-transform duration-700 ease-in-out ${isOpen ? "-translate-y-0" : "-translate-y-[105%]"
            }`}
        >
          <div className="flex items-center justify-between gap-2">
            <NavLink
              to="/"
              aria-label="Zero Typing AI home"
              onClick={() => setIsOpen(false)}
              className="w-auto"
            >
              <ReactSVG
                src={zeroTypingLogo}
                beforeInjection={(svg) => {
                  svg.setAttribute("class", "w-auto h-10");
                }}
                className="w-auto"
              />
            </NavLink>
            <FiX onClick={() => setIsOpen(false)} className="size-7" />
          </div>

          <div className="flex flex-col gap-2 mt-4 w-full font-medium font-inter">
            {visiblePages?.map((page, i) => (
              <NavLink
                key={i}
                to={page.link}
                onClick={() => setIsOpen(false)}
                className="flex items-center justify-between gap-2 text-lg text-[#35353599] py-2"
              >
                {page.name}
                <ReactSVG
                  src={gradientArrow}
                  beforeInjection={(svg) => {
                    svg.setAttribute("class", "w-auto h-4");
                  }}
                  className="w-auto"
                />
              </NavLink>
            ))}

            {/* Use Cases — accordion, since the mobile nav is a slide-out
                panel with no room for a floating dropdown. */}
            <div className="flex flex-col">
              <button
                type="button"
                onClick={() => setMobileUseCasesOpen((open) => !open)}
                aria-expanded={mobileUseCasesOpen}
                className={`flex items-center justify-between gap-2 text-lg py-2 transition-colors ${
                  mobileUseCasesOpen || isUseCaseRoute
                    ? "text-[#3AB1F6]"
                    : "text-[#35353599]"
                }`}
              >
                Use Cases
                <FiChevronDown
                  className={`size-5 transition-transform duration-200 ${
                    mobileUseCasesOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {mobileUseCasesOpen && (
                <div className="ml-1 flex flex-col border-l border-[#0000001A] pl-4">
                  {useCases.map((item) => (
                    <NavLink
                      key={item.link}
                      to={item.link}
                      onClick={() => setIsOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center justify-between gap-2 py-2.5 text-base ${
                          isActive ? "text-[#3AB1F6]" : "text-[#353535]"
                        }`
                      }
                    >
                      {item.name}
                      <FiArrowRight className="size-4 shrink-0 text-[#3AB1F6]" />
                    </NavLink>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-col items-start gap-4 text-sm font-medium">
              {isLoggedIn && !isWebAppPage && (
                <NavLink
                  to={"/app"}
                  onClick={() => setIsOpen(false)}
                  className="flex items-center justify-between gap-2 w-full"
                >
                  <div className="flex items-center gap-3">
                    <ReactSVG
                      src={sideBarLogo}
                      beforeInjection={(svg) => {
                        svg.setAttribute("class", "w-7 h-7");
                      }}
                      className="w-auto"
                    />
                    <div className="flex flex-col font-inter">
                      <span className="text-lg font-semibold text-[#353535]">
                        Web App
                      </span>
                      <span className="text-xs text-[#3535359E]">
                        Access the Zero Tying app!
                      </span>
                    </div>
                  </div>

                  <div className="pt-1">
                    <ReactSVG
                      src={gradientArrow}
                      beforeInjection={(svg) => {
                        svg.setAttribute("class", "w-auto h-4");
                      }}
                      className="w-auto"
                    />
                  </div>
                </NavLink>
              )}

              {/* Hide this entry if already on /settings */}
              {isLoggedIn && !isSettingsPage && (
                <NavLink
                  to={"/settings"}
                  onClick={() => setIsOpen(false)}
                  className="flex items-center justify-between gap-2 w-full"
                >
                  <div className="flex items-center gap-3">
                    <ReactSVG
                      src={ManageSubscription}
                      beforeInjection={(svg) => {
                        svg.setAttribute("class", "w-7 h-7");
                      }}
                      className="w-auto"
                    />
                    <div className="flex flex-col font-inter">
                      <span className="text-lg font-semibold text-[#353535]">
                        Manage Subscription
                      </span>
                      <span className="text-xs text-[#3535359E]">
                        Access billing and plan settings
                      </span>
                    </div>
                  </div>

                  <div className="pt-1">
                    <ReactSVG
                      src={gradientArrow}
                      beforeInjection={(svg) => {
                        svg.setAttribute("class", "w-auto h-4");
                      }}
                      className="w-auto"
                    />
                  </div>
                </NavLink>
              )}

              {/* Usage */}
              {isLoggedIn && !isUsagePage && (
                <NavLink
                  to={"/usage"}
                  onClick={() => setIsOpen(false)}
                  className="flex items-center justify-between gap-2 w-full"
                >
                  <div className="flex items-center gap-3">
                    <SiCircle size={26} />
                    <div className="flex flex-col font-inter">
                      <span className="text-lg font-semibold text-[#353535]">
                        Usage
                      </span>
                      <span className="text-xs text-[#3535359E]">
                        Track your plan usage and remaining limits
                      </span>
                    </div>
                  </div>

                  <div className="pt-1">
                    <ReactSVG
                      src={gradientArrow}
                      beforeInjection={(svg) => {
                        svg.setAttribute("class", "w-auto h-4");
                      }}
                      className="w-auto"
                    />
                  </div>
                </NavLink>
              )}

              <div className="h-[1px] bg-[#0000001A] w-full" />

              {isLoggedIn ? (
                <button
                  onClick={() => {
                    handleLogout();
                    setIsOpen(false);
                  }}
                  className="flex items-center gap-3 w-full rounded-xl text-[#353535] text-xl font-semibold font-inter"
                >
                  <FiLogOut className="size-6 text-[#353535]" />
                  Logout
                </button>
              ) : (
                <NavLink
                  to={"/login"}
                  onClick={() => setIsOpen(false)}
                  className="flex items-center justify-between w-full gap-2 text-lg text-[#000] font-inter font-medium py-2"
                >
                  Sign In
                  <ReactSVG
                    src={gradientArrow}
                    beforeInjection={(svg) => {
                      svg.setAttribute("class", "w-auto h-4");
                    }}
                    className="w-auto"
                  />
                </NavLink>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Navbar;
